import {
  LtlQuoteParametersViewModel,
  LtlQuoteViewModelLegacy,
  LtlTerminalInfoViewModel,
} from '@store/orders/types';
import { LineItemViewModel } from '@typeDefinitions/lineItemTypes';
import { AppliedAccessorial } from '@typeDefinitions/ltlTypes';
import { OperationTypeV4 } from '@typeDefinitions/operationTypes';
import { ItemAccessorial } from '@views/ltl/types';

export type QuoteFormState = {
  quoteParametersId: number | null;
  loadId: number | null;
  resetForm: boolean;
};

export type AccessorialState = {
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  items: ItemAccessorial[];
  overlay: boolean;
};

export type LTLQuotingResultState = {
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  items: LTLRateQuoteResponse[];
  overlay: boolean;
  customerId?: number;
};

export type QuoteSpecifics = {
  quoteResponse: LTLRateQuoteResponse;
  customerId?: number;
  requestDetails: LTLRateQuoteRequest;
};

export type TempQuoteModel = {
  quoteSpecifics: QuoteSpecifics;
  ltlQuote: LtlQuoteViewModelLegacy;
  ltlQuoteId: number | null;
};

export type LTLQuoteState = {
  isLoading: boolean;
  isError: boolean;
  requote?: boolean;
  errorMessage: string;
  quote?: LTLQuote;
  overlay: boolean;
  falveyPricingQuote?: FalveyInsuranceQuote;
  declaredValue?: number;
};

export type LTLAddressBookSnapshot = {
  addressLine1: string;
  addressLine2: string | null;
  locationName: string;
  contactName: string | null;
  contactPhone: string;
  contactEmail: string | null;
  specialInstructions: string | null;
  internalNote: string | null;
  drivingDirections: string | null;
  openTimeFrom: string;
  openTimeTo: string;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  countryCode?: string | null;
};

//this type will be the type to rule them all, and in darkness bind them
export interface LTLQuote {
  customerName?: string; // these are highly likely needed
  customerId?: number; // these are highly likely needed
  ltlQuote?: LtlQuoteViewModelLegacy; // this is from dbo.LTLQuote
  originZip?: string;
  originState?: string | null;
  originCity?: string | null;
  originCountry?: string | null;
  destinationZip?: string;
  destinationCity?: string | null;
  destinationState?: string | null;
  destinationCountry?: string | null;
  pickupDate?: Date | string | number | null;
  loadItems?: LTLLoadItem[];
  ltlQuoteId?: number | null; //This is ltl.Quote.Id
  originAddress?: LTLAddressBookSnapshot;
  destinationAddress?: LTLAddressBookSnapshot;
  originAddressMismatch?: boolean;
  destinationAddressMismatch?: boolean;
}

export type LTLLoadItem = {
  ltlQuoteLoadItemId?: string;
  nmfc: string | null;
  class?: string | null;
  item?: string; //LEGACY, replace with unitType
  unitType?: string;
  description: string;
  quantity: number | null;
  charge?: null | string;
  weight: number | null;
  height: null | string;
  length: number | null;
  width: null | string;
  volume?: null | number;
  uom: string;
  hazmat: boolean;
  heightInch: number | null; //LEGACY, replace with height
  lengthInch: number | null; //LEGACY, replace with length
  widthInch: number | null; //LEGACY, replace with width
  pieces: string | number | null;
  loadItemHazmatDetail?: LTLHazmatDetails | null; //LEGACY, replace with quoteHazmatDetail
  suggestedClass?: null | string;
  hazmatItems?: null | string;
  hazmatDescription?: null | string;
  type?: string;
  pcf?: string;
  cubicFeet?: number;
  index?: number;
  quoteLoadItemBreakdown?: LTLQuoteLoadItemBreakdown[];
  operationType?: OperationTypeV4;
};

export type LTLQuoteLoadItemBreakdown = {
  ltlQuoteLoadItemId?: string;
  quoteHazmatDetail?: LTLHazmatDetails | null; // this is coming from the backendop. We need to align this at a later point
  nmfc: string | null;
  class?: string | null;
  description: string;
  weight: number | null;
  hazmat: boolean;
  pieces: string | null;
  loadItemHazmatDetail?: LTLHazmatDetails | null;
  loadItemId?: number;
  index?: number;
};

export type LTLHazmatDetails = {
  unNumber?: string;
  packingGroup?: string;
  hazardClass?: string;
  piece?: string;
  contactNumber?: string;
  contactName?: string;
  loadItemId?: number;
  ltlCatalogId?: string;
  operationType?: OperationTypeV4;
};

export type FalveyQuotesPayload = {
  shipmentTypeID: string;
  declaredValue: number;
  currency: string;
  shippingMerchandiseDescription: string;
  ltlQuoteId?: string;
  quoteParametersId?: number;
  orderId?: number;
};

export type FalveyInsuranceQuote = {
  insurancePremium: number;
  providerFee: number;
  serviceFee: number;
  excludedItemMatch: string;
  excludedItemIDMatch: string;
};

export type FalveyInsuranceQuoteInLoad = {
  shippingSumInsured: number;
  quotePremium: number;
  quoteServiceFee: number;
};

export enum LTLReferenceNumberTypes {
  Pro = 'Pro',
  Pickup = 'Pickup',
  BOL = 'Bol',
  ProNumber = 'ProNumber',
}

export interface LTLReferenceNumber {
  type: LTLReferenceNumberTypes;
  value: string;
  internalName: string;
  displayName: string;
  sequence: number;
  modifiable: boolean;
}

export interface LTLTrackingData {
  referenceNumbers: LTLReferenceNumber[];
  trackingUrlTemplate: string;
  loadId: number;
}

export interface LTLTrackingDataState {
  isLoading: boolean;
  isSaving: boolean;
  isError: boolean;
  errorMessage: string;
  operationType: OperationTypeV4;
  trackingData: LTLTrackingData;
}
export interface LoadItemCalculationInput {
  loadItems: LoadLineItemDimensions[];
  isStackable: boolean;
  customerId?: number | null;
}
export interface LoadLineItemDimensions {
  heightInch: number | null;
  lengthInch: number | null;
  widthInch: number | null;
  quantity: number | null;
  weight: number | null;
  index: number;
  class?: string | null;
}
export interface GetCalculationsFromLoadItemsApiResponse {
  isSuccess: boolean;
  isFailure: boolean;
  message: string;
  value: LoadItemCalculationTotals;
}
/**
 * Shipment-level advisories computed by the pricing service (over-length,
 * linear feet, density, weight). These replaced the equivalent client-side
 * checks, so the UI renders whatever the API returns rather than recomputing.
 */
export interface LoadItemCalculationWarning {
  messageType: string;
  code: string;
  message: string;
  itemIndexes?: number[] | null;
}
export interface LoadItemCalculationTotals {
  totalLinearFeet: number;
  totalCubicFeet: number;
  totalPcf: number;
  totalAdjustedPcf: number;
  totalAdjustedCubicFeet: number;
  loadItemCalculations: LoadItemCalculation[];
  warnings?: LoadItemCalculationWarning[];
}
interface LoadItemCalculation {
  index: number;
  cubicFeet: number;
  pcf: number;
  adjustedCubicFeet: number;
  adjustedPcf: number;
  suggestedClass: number | null;
}
export type LoadItemCalculationState = {
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  items: LoadItemCalculationTotals;
  overlay: boolean;
};

export type AccessorialPayload = {
  id: number;
  accessorialId: string;
  accessorialName: string;
  accessorialDescription: string;
  isDelivery: boolean;
  isPickup: boolean;
  isGlobal: boolean;
  accessorialType: number;
};

export interface LTLRateQuoteRequest {
  customerId: number | null;
  customerName: string | null;
  pickupDate: string;
  originZip: string;
  originCity: string;
  originState: string;
  originCountry: string;
  destinationZip: string;
  destinationCity: string;
  destinationState: string;
  destinationCountry: string;
  loadItems: LTLLoadItem[];
  accessorials: AppliedAccessorial[];
  hasAccessorials?: boolean;
  correlationId?: string | null;
  enableCSP?: boolean;
  isStackable?: boolean;
  isLinearFeetOverridden?: boolean;
  linearFeet?: string;
  declaredValue?: number;
  serviceFee?: number;
  premiumFee?: number;
  pcf?: number;
  totalCubicFeet?: number;
}

export interface LTLRateQuoteResponse {
  quoteParametersId?: number | null;
  quoteHash: string;
  parentProvider: string;
  shipper: string;
  serviceType: string;
  netFreightCost: number;
  totalCost: number;
  costPlusId: number | null;
  costPlus: number;
  costPlusOverrideId: string | null;
  transitDays: string;
  quoteID: string;
  carrierId: string;
  originZip: string;
  destinationZip: string;
  deliveryTimestamp: string | null;
  originContact: QuoteContact;
  destinationContact: QuoteContact;
  lineItems: LineItemViewModel[];
  carrierSCAC: string;
  useCarrier: boolean;
  note: string;
  terminalLink: string;
  isCustomer: boolean;
  customerRateQuote?: LTLRateQuoteResponse;
  maxNewLiability: number | null;
  maxUsedLiability: number | null;
  errors: string[];
  exception: string;
  currencyCode: string;
  isDirectQuote: boolean;
  internalSCAC: string;
  terminalInfo: LtlTerminalInfoViewModel;
  quoteDateTimeUtc: string | null;
  lTLQuoteId: string | null;
  isSaved: boolean;
  tariffDescription: string;
  baseTariffName: string;
  baseTariffEffectiveDate: string;
  transactionId: string;
  pricingRequestType: LTLPricingRequestType | null;
  id: number | null;
  movementType?: string;
  estimatedDeliveryDate?: string;
  quoteRequestLocator?: string;
  quoteRequestId?: string;
}

export interface QuoteContact {
  personName: string;
  phoneNumber: string;
  faxNumber: string;
}

export enum LTLPricingRequestType {
  None = 0,
  Contract = 1,
  Dynamic = 2,
  Volume = 3,
}

export interface LTLTerminal {
  terminalType?: TerminalType;
  terminalName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  postalCode?: string;
  state?: string;
  countryCode?: string;
  contactName?: string;
  contactPhone?: string;
  contactTollFree?: string;
  contactFax?: string;
  contactEmail?: string;
  carrierName?: string;
  sCAC?: string;
}

enum TerminalType {
  Pickup,
  Delivery,
}

export type CarrierMaxLiability = {
  id: number;
  carrierId: number;
  customerId?: number | null;
  pricingRequestType: number;
  class: string;
  maxNew: number | null;
  maxUsed: number | null;
  createdDate?: Date | null;
  modifiedDate?: Date | null;
  modifiedBy?: number | null;
  createdBy?: number | null;
  historyContext?: string | null;
  operationType?: string | null;
};

export type CustomerInfo = {
  customerId: number;
  customerName: string;
};

export type CarrierLiabilityTypeViewModel = {
  customerSpecific: CustomerInfo[];
  carrierLiabilityTypes: string[];
};

export type ltlQuotingViewState = {
  currentLTLView: 'quote-result' | 'quote-form';
};

export interface QuoteParametersV2Params {
  isHistoryRequest: boolean;
  customerId?: number;
  sortBy?: string;
  sortDir?: 'ASC' | 'DESC';
  pageNumber: number;
  pageSize: number;
  filterCustomerName?: string;
  filterQuoteRequestLocator?: string;
  filterDateSavedFrom?: string;
  filterDateSavedTo?: string;
  filterExpDateFrom?: string;
  filterExpDateTo?: string;
  filterOriginPostal?: string;
  filterDestPostal?: string;
  filterCreatedByName?: string;
}

export interface QuoteParametersV2ApiResponse {
  quoteParameters: LtlQuoteParametersViewModel[];
  totalRecordsCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface QuoteParametersV2Response {
  data: LtlQuoteParametersViewModel[];
  totalCount: number;
}
